import React, { useState, useEffect } from 'react';
import { TrendingUp, Shield, PieChart, Brain, Target, Clock, CheckCircle, ArrowRight, AlertCircle, Globe, X, Wallet, BarChart3, DollarSign, ChevronDown, HelpCircle, CreditCard, Calendar, Users, Calculator } from 'lucide-react';
import TermsAndConditions from './components/legal/TermsAndConditions';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import FeaturesPage from './components/FeaturesPage';
import PricingPage from './components/PricingPage';
import ResourcesPage from './components/ResourcesPage';

const LandingPage = ({ onSignIn, onStartTrial, onCountrySelected, onBackToApp, currentUser, isAuthenticated }) => {
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showResources, setShowResources] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState([]);

  // Currency detection and management
  const [displayCurrency, setDisplayCurrency] = useState('US');

  // Auto-detect region based on browser locale/timezone
  useEffect(() => {
    const detectRegion = () => {
      // Check localStorage first for user preference
      const savedCurrency = localStorage.getItem('preferred_currency');
      if (savedCurrency) {
        setDisplayCurrency(savedCurrency);
        return;
      }

      // Auto-detect using browser locale
      const locale = navigator.language || navigator.userLanguage;

      if (locale.startsWith('en-GB') || locale.startsWith('en-UK')) {
        setDisplayCurrency('UK');
      } else if (locale.startsWith('en-US') || locale.startsWith('en')) {
        setDisplayCurrency('US');
      } else {
        // Default to US for other locales
        setDisplayCurrency('US');
      }
    };

    detectRegion();
  }, []);

  // Pricing configuration
  const pricing = {
    US: {
      symbol: '$',
      free: '0',
      premium: '9.99',
      currency: 'USD'
    },
    UK: {
      symbol: '£',
      free: '0',
      premium: '7.99',
      currency: 'GBP'
    }
  };

  const currentPricing = pricing[displayCurrency];

  const handleCurrencyToggle = (currency) => {
    setDisplayCurrency(currency);
    localStorage.setItem('preferred_currency', currency);
  };

  const handleGetStarted = () => {
    setShowCountryModal(true);
  };

  const handleCountrySelect = (countryCode) => {
    setSelectedCountry(countryCode);
    // Sync currency display with country selection
    setDisplayCurrency(countryCode);
    localStorage.setItem('preferred_currency', countryCode);
  };

  const handleContinue = () => {
    if (selectedCountry) {
      onCountrySelected(selectedCountry);
      setShowCountryModal(false);
      onStartTrial();
    }
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const toggleCategory = (categoryIndex) => {
    if (expandedCategories.includes(categoryIndex)) {
      setExpandedCategories(expandedCategories.filter(i => i !== categoryIndex));
    } else {
      setExpandedCategories([...expandedCategories, categoryIndex]);
    }
  };

  // FAQ Data
  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          q: "How do I get started with PlanLens?",
          a: "Getting started is easy! Click 'Get Started' and select your region (US or UK). Create a free account, then add your financial accounts and investments. The dashboard will automatically calculate your portfolio value and provide insights."
        },
        {
          q: "Is PlanLens really free?",
          a: "Yes! The free plan includes portfolio tracking, multi account types, multi-currency support, financial goals tracking & projections, and CSV export. You can use these features forever without a credit card. Premium features (AI-powered insights & recommendations, Enhanced Retirement Planning, Net Worth Dashboard, Liabilities Management, Monte Carlo simulations, backtesting) are available for $5.99/month (USD) or £4.99/month (GBP)."
        },
        {
          q: "What account types are supported?",
          a: "We support multi account types: US accounts include 401(k), Roth IRA, Traditional IRA, SEP IRA, HSA, 529 Plan, and Taxable Brokerage. UK accounts include Stocks & Shares ISA, Cash ISA, Lifetime ISA, Junior ISA, Workplace Pension, SIPP, Defined Benefit Pension, and General Investment Account."
        }
      ]
    },
    {
      category: "Features & Usage",
      questions: [
        {
          q: "What asset classes can I track?",
          a: "PlanLens tracks 6 asset classes: Stocks, Bonds, Real Estate, Commodities, Cryptocurrency, and Cash. You can view your portfolio by account, by asset class, or see your complete portfolio with beautiful visualizations."
        },
        {
          q: "How does retirement insights work?",
          a: "Our retirement insights uses your actual portfolio data to project future growth. You can set your retirement age, desired spending, and see drawdown simulations with success rates across different scenarios. The tool helps you understand if you're on track to meet your retirement goals."
        },
        {
          q: "What is Monte Carlo simulation?",
          a: "Monte Carlo simulation (Premium feature) runs up to 10,000 different market scenarios to project your portfolio's future performance. This shows you the range of possible outcomes (best case, median, worst case) and helps you understand investment risk and potential returns."
        },
        {
          q: "What does the AI Financial Health Check do?",
          a: "The AI Financial Health Check (Premium) provides comprehensive analysis of your complete financial picture: portfolio allocation and diversification, net worth breakdown with debt ratios, debt payoff strategies (avalanche vs snowball), goal-based asset allocation recommendations, emergency fund adequacy assessment, and a personalized financial priority action plan. All analysis includes educational insights explaining concepts in simple terms with real examples."
        }
      ]
    },
    {
      category: "Premium & Pricing",
      questions: [
        {
          q: "What's included in the Premium plan?",
          a: "Premium includes: Monte Carlo simulations (up to 10,000 scenarios), Historical backtesting (20+ years), AI-powered portfolio insights & recommendations, Risk analysis (volatility, Sharpe ratio), Diversification scoring, and Priority support & early access to new features."
        },
        {
          q: "Why are US and UK prices different?",
          a: "Prices are adjusted for each market based on currency exchange rates and local market conditions. US pricing is $5.99/month or $65/year, and UK pricing is £4.99/month or £49.99/year. Both plans include exactly the same premium features."
        },
        {
          q: "Can I cancel anytime?",
          a: "Yes! You can cancel your Premium subscription at any time from your Profile settings. There are no cancellation fees, and you'll retain access to Premium features until the end of your billing period."
        },
        {
          q: "Can I export my data?",
          a: "Yes! Both Free and Premium plans include CSV export functionality while PDF is only available with the Premium plan. You can download your portfolio data, analysis reports, and retirement projections at any time."
        }
      ]
    },
    {
      category: "Security & Privacy",
      questions: [
        {
          q: "Is my financial data secure?",
          a: "Absolutely. We use industry-standard encryption to protect your data. Your portfolio information is stored securely and never shared with third parties. We follow strict security protocols and best practices to keep your information safe."
        },
        {
          q: "Do you provide financial advice?",
          a: "No. PlanLens is an educational and planning tool only. We do not provide financial advice, investment recommendations, or professional financial services. For personalized financial advice, please consult a qualified Independent Financial Advisor (IFA) or licensed financial professional."
        },
        {
          q: "What data do you collect?",
          a: "We collect only the information necessary to provide our services: your email, country selection, and the portfolio data you choose to enter. We don't sell your data or use it for advertising. See our Privacy Policy for complete details."
        }
      ]
    },
    {
      category: "Technical & Support",
      questions: [
        {
          q: "Which countries are supported?",
          a: "Currently, PlanLens fully supports the United States and United Kingdom with country-specific account types, tax considerations, and currency (USD/GBP). We plan to expand to more countries in the future."
        },
        {
          q: "Can I use this on mobile devices?",
          a: "Yes! PlanLens is fully responsive and works on desktop, tablet, and mobile devices. You can access your portfolio and planning tools from any device with a web browser."
        },
        {
          q: "How do I get help or support?",
          a: "Free users can access our comprehensive Help & Learn center within the app. Premium users receive priority email support and faster response times. Visit the Help tab in the app for tutorials, guides, and answers to common questions."
        },
        {
          q: "How often is data updated?",
          a: "Your portfolio values are calculated instantly from the investment amounts you enter. All investments and cryptocurrency holdings are manually entered by you - we don't automatically fetch live market prices. To keep your portfolio current, simply update your investment amounts as their values change. This gives you full control over your data."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Navigation Header */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">PlanLens</span>
            </div>

            {/* Center Navigation Tabs */}
            <div className="hidden md:flex items-center space-x-6">
              <button
                onClick={() => setShowFeatures(true)}
                className="text-gray-700 hover:text-blue-600 font-medium transition"
              >
                Features
              </button>
              <button
                onClick={() => setShowPricing(true)}
                className="text-gray-700 hover:text-blue-600 font-medium transition"
              >
                Pricing
              </button>
              <button
                onClick={() => setShowResources(true)}
                className="text-gray-700 hover:text-blue-600 font-medium transition"
              >
                Resources
              </button>
              <a
                href="/privacy-policy"
                className="text-gray-700 hover:text-blue-600 font-medium transition"
              >
                Privacy
              </a>
            </div>

            {/* Right Side - Sign In / Back to App */}
            {isAuthenticated ? (
              <button
                onClick={onBackToApp}
                className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition shadow-md hover:shadow-lg"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                <span>Back to App</span>
              </button>
            ) : (
              <button
                onClick={onSignIn}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full mb-6">
            <Brain className="w-4 h-4" />
            <span className="text-sm font-semibold">AI-Powered Financial, Investment and Retirement Insights</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Your Journey to
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Financial Freedom</span>
          </h1>

          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Track investments, Analyze retirement, and get AI-powered insights—all in one intelligent platform designed to help you achieve your financial goals.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-12">
            {isAuthenticated ? (
              <button
                onClick={onBackToApp}
                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
              >
                <ArrowRight className="w-5 h-5 rotate-180" />
                <span>Return to Your Dashboard</span>
              </button>
            ) : (
              <button
                onClick={handleGetStarted}
                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
              >
                <span>Start Free Today</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
              className="w-full sm:w-auto px-8 py-4 bg-white text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition border-2 border-gray-200"
            >
              Learn More
            </button>
          </div>
        </div>
      </div>

      {/* Features Showcase with Marketing Images */}
      <div className="py-20 bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Experience the Power of <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">PlanLens</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Visualize your financial future with intelligent insights, comprehensive tracking, and powerful planning tools
            </p>
          </div>

          <div className="space-y-24">
            {/* AI-Powered Investment Insights */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full mb-4">
                  <Brain className="w-4 h-4" />
                  <span className="text-sm font-semibold">AI-Powered Analysis</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  AI-Powered <span className="text-blue-600">Investment Insights</span>
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Get personalized portfolio recommendations powered by AI. Our intelligent system analyzes your investments and provides actionable insights to optimize your withdrawal strategy, maximize growth potential, and complete your financial picture.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700"><strong className="text-gray-900">Optimize Withdrawal Strategy:</strong> Dynamic guardrails for better results</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700"><strong className="text-gray-900">Maximize Growth Potential:</strong> Aggressive allocation recommendations</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700"><strong className="text-gray-900">Complete Financial Picture:</strong> Link all accounts for comprehensive analysis</span>
                  </li>
                </ul>
              </div>
              <div className="order-1 lg:order-2">
                <div className="relative group">
                  <div className="absolute -inset-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl opacity-20 blur-2xl group-hover:opacity-30 transition-opacity"></div>
                  <img
                    src="/landing-page-images/ai-powered-insights.png"
                    alt="AI-Powered Investment Insights Dashboard showing portfolio growth projection and personalized recommendations"
                    className="relative rounded-2xl shadow-2xl w-full h-auto transform group-hover:scale-[1.02] transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            {/* Track Net Worth 360° */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-1">
                <div className="relative group">
                  <div className="absolute -inset-4 bg-gradient-to-r from-green-600 to-blue-600 rounded-3xl opacity-20 blur-2xl group-hover:opacity-30 transition-opacity"></div>
                  <img
                    src="/landing-page-images/net-worth-tracking.png"
                    alt="Net Worth 360° Dashboard showing total assets, liabilities, and net worth visualization"
                    className="relative rounded-2xl shadow-2xl w-full h-auto transform group-hover:scale-[1.02] transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="order-2">
                <div className="inline-flex items-center space-x-2 bg-green-100 text-green-700 px-4 py-2 rounded-full mb-4">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm font-semibold">Complete Financial View</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Track Net Worth <span className="text-green-600">360°</span>
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Visualize your complete financial picture with real-time net worth tracking. Monitor total assets, liabilities, and see your financial health score at a glance with beautiful, intuitive visualizations.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <DollarSign className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700"><strong className="text-gray-900">Total Assets Tracking:</strong> Monitor all your investments and accounts</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CreditCard className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700"><strong className="text-gray-900">Liabilities Management:</strong> Track debts and payment progress</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <PieChart className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700"><strong className="text-gray-900">Financial Health Score:</strong> Instant insights into your financial wellness</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Secure Your Future - Retirement Planning */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="inline-flex items-center space-x-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full mb-4">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm font-semibold">Retirement Planning</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Secure Your <span className="text-purple-600">Future</span>
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Plan your retirement with confidence using advanced Monte Carlo simulations. Visualize income vs expenses over time, understand your success rate, and see how many years your portfolio can sustain your retirement goals.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <BarChart3 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700"><strong className="text-gray-900">Monte Carlo Simulations:</strong> 10,000+ scenarios for accurate projections</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Target className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700"><strong className="text-gray-900">Success Rate Analysis:</strong> Know your probability of reaching goals</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Clock className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700"><strong className="text-gray-900">Years Covered:</strong> See how long your portfolio will last</span>
                  </li>
                </ul>
              </div>
              <div className="order-1 lg:order-2">
                <div className="relative group">
                  <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl opacity-20 blur-2xl group-hover:opacity-30 transition-opacity"></div>
                  <img
                    src="/landing-page-images/retirement-planning.png"
                    alt="Retirement Planning Dashboard with Monte Carlo success rate and income vs expenses projection"
                    className="relative rounded-2xl shadow-2xl w-full h-auto transform group-hover:scale-[1.02] transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-20">
            <button
              onClick={handleGetStarted}
              className="px-12 py-5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition shadow-2xl hover:shadow-3xl text-lg inline-flex items-center space-x-3"
            >
              <span>Start Your Financial Journey Today</span>
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="text-sm text-gray-500 mt-4">No credit card required • Free forever plan available</p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Powerful Features for Smart Financial Analysis</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to take control of your financial future
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Net Worth Dashboard */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 hover:shadow-lg transition">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mb-3">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Net Worth Tracking</h3>
              <p className="text-sm text-gray-700">
                Complete 360° view of your financial health with real-time calculations, health scoring, and visual breakdowns.
              </p>
            </div>

            {/* Financial Goals */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 hover:shadow-lg transition">
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center mb-3">
                <Target className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Financial Goals</h3>
              <p className="text-sm text-gray-700">
                Set and track goals with intelligent projections, progress tracking, and automated savings calculations.
              </p>
            </div>

            {/* Liabilities Management */}
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 hover:shadow-lg transition">
              <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center mb-3">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Debt Management</h3>
              <p className="text-sm text-gray-700">
                Track mortgages, loans, and credit cards with payoff progress visualization and payment tracking.
              </p>
            </div>

            {/* Portfolio Tracking */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 hover:shadow-lg transition">
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center mb-3">
                <PieChart className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Portfolio Tracking</h3>
              <p className="text-sm text-gray-700">
                Track investments across 6 asset classes with beautiful visualizations and account-level breakdowns.
              </p>
            </div>

            {/* Account Management */}
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-6 hover:shadow-lg transition">
              <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center mb-3">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Account Management</h3>
              <p className="text-sm text-gray-700">
                Manage 9 US and 10 UK account types including 401(k), IRA, ISA, SIPP with tax-advantaged tracking.
              </p>
            </div>

            {/* Enhanced Retirement Insights */}
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6 hover:shadow-lg transition">
              <div className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center mb-3">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Enhanced Retirement Insights</h3>
              <p className="text-sm text-gray-700">
                Advanced retirement calculator with scenario analysis, AI-powered insights, and visual projections to help you plan for a secure retirement.
              </p>
            </div>

            {/* Gemini AI-Powered Insights */}
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl p-6 hover:shadow-lg transition">
              <div className="w-10 h-10 bg-pink-600 rounded-lg flex items-center justify-center mb-3">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">AI Financial Health Check</h3>
              <p className="text-sm text-gray-700">
                Powered by Google's Gemini AI to analyze your complete financial picture: portfolio, net worth, debt strategies, goal allocation, emergency fund, and personalized priority action plans. Educational insights explain complex concepts in simple terms.
              </p>
            </div>

            {/* Portfolio Analysis */}
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6 hover:shadow-lg transition">
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center mb-3">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Portfolio Analysis</h3>
              <p className="text-sm text-gray-700">
                Run Monte Carlo simulations and historical backtesting with risk-adjusted metrics.
              </p>
            </div>

            {/* Multi-Country Support */}
            <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl p-6 hover:shadow-lg transition">
              <div className="w-10 h-10 bg-cyan-600 rounded-lg flex items-center justify-center mb-3">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Multi-Country</h3>
              <p className="text-sm text-gray-700">
                Built for US & UK with country-specific accounts, tax rules, and currency conversion.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tools & Resources Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Free Tools & Resources</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Explore our free financial planning tools and guides tailored for US & UK
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Privacy-First Portfolio Tracker */}
            <a
              href="/privacy-first-portfolio-tracker"
              className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 hover:shadow-lg transition group"
            >
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-green-600 transition">
                Privacy-First Tracker
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Learn how PlanLens protects your financial data with zero data sharing
              </p>
              <div className="flex items-center text-green-600 font-semibold text-sm">
                <span>Learn More</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </a>

            {/* US Retirement Calculator */}
            <a
              href="/calculators/us-retirement-calculator"
              className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 hover:shadow-lg transition group border-2 border-blue-200"
            >
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <div className="flex items-center mb-2">
                <span className="text-2xl mr-2">🇺🇸</span>
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition">
                  US Retirement
                </h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                401(k), IRA, Roth IRA, and Social Security calculator
              </p>
              <div className="flex items-center text-blue-600 font-semibold text-sm">
                <span>Calculate Now</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </a>

            {/* UK Pension Calculator */}
            <a
              href="/calculators/uk-pension-calculator"
              className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 hover:shadow-lg transition group border-2 border-purple-200"
            >
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mb-4">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <div className="flex items-center mb-2">
                <span className="text-2xl mr-2">🇬🇧</span>
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-purple-600 transition">
                  UK Pension
                </h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                SIPP, Workplace, Defined Benefit, and State Pension
              </p>
              <div className="flex items-center text-purple-600 font-semibold text-sm">
                <span>Calculate Now</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </a>

            {/* Financial Planner vs Software */}
            <a
              href="/compare/financial-planner-vs-software"
              className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6 hover:shadow-lg transition group"
            >
              <div className="w-12 h-12 bg-amber-600 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition">
                Planner vs Software
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Compare costs and benefits of financial planners vs software
              </p>
              <div className="flex items-center text-amber-600 font-semibold text-sm">
                <span>Compare Options</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Ready to Take Control of Your Financial Future?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join thousands of users planning smarter and reaching their goals faster
          </p>
          <button
            onClick={handleGetStarted}
            className="px-12 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition shadow-xl hover:shadow-2xl text-lg"
          >
            Get Started Free Today
          </button>
          <p className="text-sm text-gray-500 mt-4">No credit card required • Start in seconds</p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold">PlanLens</span>
              </div>
              <p className="text-gray-400 mb-4 max-w-md">
                Your intelligent partner in financial Analasyis and Planning. Track, plan, and optimize your path to financial freedom.
              </p>
              <p className="text-xs text-gray-500">
                © 2025 PlanLens. All rights reserved.
              </p>
            </div>
            
            <div className="col-span-1 md:col-span-2">
              <h4 className="font-semibold mb-4">Important Information</h4>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-gray-400 text-sm">
                <a
                  href="/terms"
                  className="hover:text-white transition text-gray-400"
                >
                  Terms & Conditions
                </a>
                <span className="text-gray-600">•</span>
                <a
                  href="/privacy-policy"
                  className="hover:text-white transition text-gray-400"
                >
                  Privacy Policy
                </a>
                <span className="text-gray-600">•</span>
                <button
                  onClick={() => document.getElementById('disclaimer')?.scrollIntoView({ behavior: 'smooth' })}
                  className="hover:text-white transition bg-transparent border-0 cursor-pointer p-0 text-left text-gray-400"
                >
                  Disclaimer
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-600 mt-8 pt-8" id="disclaimer">
            <div className="max-w-4xl mx-auto">
              <p className="text-center text-xs text-gray-300 mb-4">
                <strong className="text-gray-300">Important Disclaimer:</strong> PlanLens is an educational, Analasyis and planning tool only. This platform does not provide financial advice, investment recommendations, or professional financial services.
              </p>
              <p className="text-center text-xs text-gray-300">
                For personalized financial advice, please consult with a qualified Independent Financial Advisor (IFA) or licensed financial professional. Past performance does not guarantee future results.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Legal Modals */}
      {showTerms && <TermsAndConditions onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}

      {/* New Feature Modals */}
      {showFeatures && <FeaturesPage onClose={() => setShowFeatures(false)} />}
      {showPricing && (
        <PricingPage
          onClose={() => setShowPricing(false)}
          selectedCountry={displayCurrency === 'US' ? 'us' : 'uk'}
          currentUser={currentUser}
          onSignIn={onSignIn}
        />
      )}
      {showResources && (
        <ResourcesPage
          onClose={() => setShowResources(false)}
          onNavigateToSupport={() => {
            // Close resources modal - user can access support from main app after signing in
            setShowResources(false);
            // In main app context, this would navigate to support tab
          }}
        />
      )}

      {/* Country Selection Modal */}
      {showCountryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-8 relative animate-fade-in">
            <button
              onClick={() => setShowCountryModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Select Your Region</h2>
              <p className="text-gray-600">
                Choose your country to personalize your experience with the correct currency and account types.
              </p>
            </div>

            <div className="space-y-3 mb-6">
              {/* United States Option */}
              <button
                onClick={() => handleCountrySelect('US')}
                className={`w-full p-6 rounded-xl border-2 transition-all text-left ${
                  selectedCountry === 'US'
                    ? 'border-blue-600 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="text-4xl">🇺🇸</div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">United States</h3>
                      <p className="text-sm text-gray-600">Currency: USD ($)</p>
                      <p className="text-xs text-gray-500 mt-1">401(k), IRA, HSA, and more</p>
                    </div>
                  </div>
                  {selectedCountry === 'US' && (
                    <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  )}
                </div>
              </button>

              {/* United Kingdom Option */}
              <button
                onClick={() => handleCountrySelect('UK')}
                className={`w-full p-6 rounded-xl border-2 transition-all text-left ${
                  selectedCountry === 'UK'
                    ? 'border-blue-600 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="text-4xl">🇬🇧</div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">United Kingdom</h3>
                      <p className="text-sm text-gray-600">Currency: GBP (£)</p>
                      <p className="text-xs text-gray-500 mt-1">ISA, SIPP, Pensions, and more</p>
                    </div>
                  </div>
                  {selectedCountry === 'UK' && (
                    <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  )}
                </div>
              </button>
            </div>

            <button
              onClick={handleContinue}
              disabled={!selectedCountry}
              className={`w-full py-4 rounded-xl font-semibold transition-all ${
                selectedCountry
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Continue
            </button>

            <p className="text-center text-xs text-gray-500 mt-4">
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
